import type { VacationRequest } from '@domain/entities/VacationRequest';

/**
 * Rescheduling produces a chain, and a chain is one story.
 *
 * VC-0004 is refused, VC-0005 replaces it and is refused too, VC-0006 replaces
 * that. Three documents — the server cannot rewrite a decided request, and it
 * should not: a rejection is evidence and the audit log and the emails already
 * quote those folios. But three *cards* is a different claim, and it is the
 * wrong one: the list read as somebody who had asked for holiday three times
 * rather than once, three times over.
 *
 * So the chain collapses to its head — the request that is still live, or the
 * last attempt if none is — and the ones it replaced ride along as history.
 * Nothing is dropped and nothing is merged in the database; only the reading
 * changes.
 */

export interface RescheduleChain {
  /** The current state of this story: the last request in the chain. */
  readonly head: VacationRequest;
  /** What it replaced, most recent first. Empty for a request nobody rescheduled. */
  readonly history: readonly VacationRequest[];
}

export const groupRescheduleChains = (
  requests: readonly VacationRequest[],
): readonly RescheduleChain[] => {
  const byFolio = new Map(requests.map((request) => [request.folio, request]));

  /*
   * Which requests were replaced by another one *that is in this list*.
   *
   * The qualifier matters: the page is capped, so a chain can reach past what
   * was loaded. A request whose successor is not here has to keep its own card
   * — hiding it because something invisible replaced it would leave the reader
   * with a story that has no visible ending.
   */
  const replaced = new Set<string>();
  for (const request of requests) {
    const from = request.rescheduledFromFolio;
    // Nothing replaces itself. Without this guard a row pointing at its own
    // folio marks itself as superseded and vanishes from the list altogether —
    // a corrupt field would take the request off the screen instead of merely
    // failing to link it.
    if (from !== null && from !== request.folio && byFolio.has(from)) replaced.add(from);
  }

  const chains: RescheduleChain[] = [];

  for (const request of requests) {
    if (replaced.has(request.folio)) continue;

    const history: VacationRequest[] = [];
    let cursor = request.rescheduledFromFolio;
    // A malformed cycle would spin here, so the walk is bounded by the list.
    const seen = new Set<string>([request.folio]);

    while (cursor !== null && !seen.has(cursor)) {
      const previous = byFolio.get(cursor);
      if (previous === undefined) break;
      history.push(previous);
      seen.add(cursor);
      cursor = previous.rescheduledFromFolio;
    }

    chains.push({ head: request, history });
  }

  return chains;
};
